import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";

/**
 * NounCandidatePopover
 *
 * Controlled popover — caller owns visibility (mounts/unmounts this component).
 * Position is supplied as {top, left} viewport coordinates (use getBoundingClientRect).
 * Rendered via ReactDOM.createPortal so it escapes any overflow:hidden ancestor.
 *
 * Props:
 *   detection   — the detection object (with .candidates, .nounType, .span, .confidence)
 *   position    — { top, left } in viewport px
 *   onMouseEnter — call to cancel the caller's hide timer
 *   onMouseLeave — call to restart the caller's hide timer
 *   onClose     — call to immediately hide (used after selection)
 *   currentText — full textarea value (needed for replacement)
 *   onReplace   — (newText: string) => void
 *   rootOU      — for analytics
 */
const NounCandidatePopover = ({
  detection,
  position,
  onClose,
  currentText,
  onReplace,
  rootOU,
}) => {
  const acceptedRef = useRef(false);

  // Analytics: fire "shown" on mount, "dismissed" on unmount unless a selection was made
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.debug("[NounPopover] shown", detection?.nounType, detection?.span);
    return () => {
      if (!acceptedRef.current) {
        // eslint-disable-next-line no-console
        console.debug("[NounPopover] dismissed", detection?.nounType, detection?.span);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (candidate) => {
    if (!detection || currentText == null) return;
    const before = currentText.slice(0, detection.startIndex);
    const after = currentText.slice(detection.endIndex);
    const newText = `${before}${candidate.text}${after}`;
    // eslint-disable-next-line no-console
    console.debug("[NounPopover] accepted", candidate.text);
    acceptedRef.current = true;
    onClose();
    // Pass detection and candidateText so the caller can update detections
    // in-place without triggering a full re-scan.
    onReplace(newText, detection, candidate.text);
  };

  if (!detection?.candidates?.length) return null;

  const labelMap = {
    DriverName: "Drivers",
    VehicleName: "Vehicles",
    VehiclePlate: "Vehicles",
    DepotOU: "Depots",
  };
  // VehicleName and VehiclePlate are searched together (format ambiguity),
  // so use a neutral label instead of committing to one type.
  const isVehicleType = detection.nounType === "VehicleName" || detection.nounType === "VehiclePlate";
  const nounTypeLabel = isVehicleType ? "Did you mean?" : (labelMap[detection.nounType] ?? detection.nounType);

  return ReactDOM.createPortal(
    <div
      className="fixed bg-white border border-[#E5E7EB] rounded-xl shadow-lg min-w-[180px] py-2 text-sm"
      style={{ top: position.top, left: position.left, zIndex: 9999 }}
      data-noun-popover="true"
    >
      <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-[#9CA3AF]">
        {nounTypeLabel}
      </div>
      {detection.candidates.map((c, i) => (
        <button
          key={i}
          type="button"
          className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-[#F5F3FF] text-[#374151] transition-colors"
          // onMouseDown so the button fires before the textarea loses focus (blur)
          onMouseDown={(e) => {
            e.preventDefault();
            handleSelect(c);
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#C4C2F4", flexShrink: 0, display: "inline-block" }} />
          <span className="truncate max-w-[160px]">{c.text}</span>
        </button>
      ))}
    </div>,
    document.body
  );
};

export default NounCandidatePopover;
